/** Staff RBAC catalog — mirrors public.permission_resource / permission_action */

export const PERMISSION_ACTIONS = [
  'view',
  'create',
  'update',
  'delete',
  'finalize',
  'amend',
] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

/** Actions shown for every resource in the roles matrix unless overridden. */
export const PERMISSION_MATRIX_ACTIONS = ['view', 'create', 'update', 'delete'] as const;

/**
 * Resources shown in the roles matrix and used by hasPermission().
 * Kept aligned with the current main menu + settings IA.
 */
export const PERMISSION_RESOURCES = [
  // Left main navigation (Hovedmenu)
  'nav_dashboard',
  'nav_patients',
  'nav_messages',
  'nav_referrals',
  'nav_medcom',
  'nav_appointments',
  'nav_economy',
  'nav_help',
  'nav_settings',
  // Clinical
  'patients',
  'patients_cpr',
  'appointments',
  'treatment_courses',
  'staff_calendars',
  'referrals',
  'documents',
  'journal',
  'journal_phrases',
  'dental',
  'communication',
  'messages',
  'sms',
  'patient_email',
  'tasks',
  'consent',
  'relations',
  'patient_assignments',
  'patient_my_filter',
  'medcom',
  'medcom_xdis',
  'medcom_ggop',
  'medcom_sup',
  // Finance
  'invoices',
  'payments',
  'quotes',
  'punch_cards',
  'reports',
  // Integrations hub
  'questionnaires',
  // Patient tab. Distinct from finance `reports`.
  'patient_reports',
  'sygeforsikring',
  // Settings / admin
  'settings_general',
  'settings_users',
  'settings_roles',
  'settings_billing',
  'online_booking',
  'multi_clinic',
  'audit_logs',
] as const;

export type PermissionResource = (typeof PERMISSION_RESOURCES)[number];

export const NAV_PERMISSION_RESOURCES = [
  'nav_dashboard',
  'nav_patients',
  'nav_messages',
  'nav_referrals',
  'nav_medcom',
  'nav_appointments',
  'nav_economy',
  'nav_help',
  'nav_settings',
] as const;

export type NavPermissionResource = (typeof NAV_PERMISSION_RESOURCES)[number];

/** Old patient-communication tab keys. Any view maps to communication.view. */
export const LEGACY_COMMUNICATION_TAB_RESOURCES = ['sms', 'messages', 'patient_email'] as const;

export type LegacyCommunicationTabResource = (typeof LEGACY_COMMUNICATION_TAB_RESOURCES)[number];

/** Hidden from the roles matrix — key still exists for email-history RPC fallback. */
export const DEPRECATED_MATRIX_RESOURCES = new Set<PermissionResource>(['patient_email']);

/** Extra matrix columns that apply only to specific resources. */
export const RESOURCE_EXTRA_ACTIONS: Partial<Record<PermissionResource, PermissionAction[]>> = {
  journal: ['finalize', 'amend'],
};

/** Resources that only expose the Se column in the matrix. */
export const VIEW_ONLY_RESOURCES = new Set<PermissionResource>([
  ...NAV_PERMISSION_RESOURCES,
  'communication',
  'patient_my_filter',
  'medcom_sup',
  'patient_reports',
]);

const RESOURCE_ACTION_OVERRIDE: Partial<Record<PermissionResource, PermissionAction[]>> = {
  journal_phrases: ['view', 'create', 'update', 'delete'],
  patient_assignments: ['view', 'update'],
  medcom: ['view', 'create'],
  medcom_xdis: ['view', 'create'],
  medcom_ggop: ['view', 'create'],
};

export function actionsForResource(resource: PermissionResource): PermissionAction[] {
  const override = RESOURCE_ACTION_OVERRIDE[resource];
  if (override) return [...override, ...(RESOURCE_EXTRA_ACTIONS[resource] ?? [])];
  if (VIEW_ONLY_RESOURCES.has(resource)) return ['view'];
  return [...PERMISSION_MATRIX_ACTIONS, ...(RESOURCE_EXTRA_ACTIONS[resource] ?? [])];
}

/** Compact map baked into JWT membership claims: { patients: ['view','create'], ... } */
export type PermissionMap = Partial<Record<PermissionResource, PermissionAction[]>>;

/** Matrix UI groups — Hovedmenu first, then features, then settings children. */
export const PERMISSION_RESOURCE_GROUPS: Array<{
  groupKey: string;
  resources: PermissionResource[];
}> = [
  {
    groupKey: 'nav',
    resources: [...NAV_PERMISSION_RESOURCES],
  },
  {
    groupKey: 'main',
    resources: [
      'patients',
      'patients_cpr',
      'appointments',
      'staff_calendars',
      'referrals',
      'documents',
      'journal',
      'journal_phrases',
      'dental',
    ],
  },
  {
    groupKey: 'patient_details',
    resources: [
      'communication',
      'messages',
      'sms',
      'tasks',
      'consent',
      'relations',
      'patient_assignments',
      'patient_my_filter',
      'treatment_courses',
      'patient_reports',
    ],
  },
  {
    groupKey: 'medcom_group',
    resources: ['medcom', 'medcom_xdis', 'medcom_ggop', 'medcom_sup'],
  },
  {
    groupKey: 'finance',
    resources: ['invoices', 'payments', 'quotes', 'punch_cards', 'reports'],
  },
  {
    groupKey: 'integrations',
    resources: ['questionnaires', 'sygeforsikring'],
  },
  {
    groupKey: 'settings',
    resources: [
      'settings_general',
      'settings_users',
      'settings_roles',
      'settings_billing',
      'online_booking',
      'multi_clinic',
      'audit_logs',
    ],
  },
];

/** Resources that grant access to the Economy section (any view). */
export const ECONOMY_PERMISSION_RESOURCES: PermissionResource[] = [
  'invoices',
  'payments',
  'quotes',
  'reports',
];

/** Resources that grant access to the Integrations settings hub (any view). */
export const INTEGRATIONS_PERMISSION_RESOURCES: PermissionResource[] = [
  'payments',
  'questionnaires',
  'sms',
  'sygeforsikring',
  'settings_general',
];

export const CLINIC_ADMIN_LOCKED_GRANTS: ReadonlyArray<{
  resource: PermissionResource;
  action: PermissionAction;
}> = [
  { resource: 'nav_settings', action: 'view' },
  { resource: 'settings_general', action: 'view' },
  { resource: 'settings_roles', action: 'view' },
  { resource: 'settings_roles', action: 'update' },
];

export function isClinicAdminLockedGrant(
  resource: PermissionResource,
  action: PermissionAction,
): boolean {
  return CLINIC_ADMIN_LOCKED_GRANTS.some((g) => g.resource === resource && g.action === action);
}

export function applyClinicAdminLockout(map: PermissionMap): PermissionMap {
  const next: PermissionMap = { ...map };
  for (const { resource, action } of CLINIC_ADMIN_LOCKED_GRANTS) {
    const actions = new Set(next[resource] ?? []);
    actions.add(action);
    next[resource] = Array.from(actions) as PermissionAction[];
  }
  return next;
}

/** Map sidebar / routes to permission resources for view gating */
export const ROUTE_PERMISSION_RESOURCE: Record<string, PermissionResource> = {
  '/dashboard': 'nav_dashboard',
  '/patients': 'patients',
  '/henvisninger': 'referrals',
  '/care-communications': 'medcom',
  '/medcom': 'medcom',
  '/appointments': 'appointments',
  '/economy/invoices': 'invoices',
  '/economy/payments': 'payments',
  '/economy/quotes': 'quotes',
  '/economy/reports': 'reports',
  '/economy': 'invoices',
  '/settings/sms': 'sms',
  '/settings/payments': 'payments',
  '/settings/questionnaires': 'questionnaires',
  '/settings/sygeforsikring': 'sygeforsikring',
  '/settings/journal-phrases': 'journal_phrases',
  '/settings/file-categories': 'documents',
  '/help': 'nav_help',
};

export type NavMenuItem = {
  id: NavPermissionResource;
  path: string;
  i18nKey: string;
  /** Feature permission that also gated the item before nav_* existed. */
  legacyFeature?: PermissionResource;
  /** Economy hub used any-of finance views. */
  legacyAnyOf?: readonly PermissionResource[];
  /** Always visible today (no feature permission). */
  legacyAlways?: boolean;
};

export const NAV_MENU_ITEMS: readonly NavMenuItem[] = [
  { id: 'nav_dashboard', path: '/dashboard', i18nKey: 'nav.dashboard', legacyAlways: true },
  { id: 'nav_patients', path: '/patients', i18nKey: 'nav.patients', legacyFeature: 'patients' },
  { id: 'nav_messages', path: '/patients/messages', i18nKey: 'nav.messages', legacyFeature: 'messages' },
  { id: 'nav_referrals', path: '/henvisninger', i18nKey: 'nav.referrals', legacyFeature: 'referrals' },
  { id: 'nav_medcom', path: '/medcom', i18nKey: 'nav.medcom', legacyFeature: 'patients' },
  {
    id: 'nav_appointments',
    path: '/appointments',
    i18nKey: 'nav.appointments',
    legacyFeature: 'appointments',
  },
  {
    id: 'nav_economy',
    path: '/economy/invoices',
    i18nKey: 'nav.economy',
    legacyAnyOf: ECONOMY_PERMISSION_RESOURCES,
  },
  { id: 'nav_help', path: '/help', i18nKey: 'nav.help', legacyAlways: true },
  { id: 'nav_settings', path: '/settings', i18nKey: 'nav.settings', legacyFeature: 'settings_general' },
];

export function mapHasResourceKey(
  map: PermissionMap | null | undefined,
  resource: PermissionResource,
): boolean {
  if (!map) return false;
  return Object.prototype.hasOwnProperty.call(map, resource) && map[resource] !== undefined;
}

export function permissionMapHasExact(
  map: PermissionMap | null | undefined,
  resource: PermissionResource,
  action: PermissionAction,
): boolean {
  if (!map) return false;
  const actions = map[resource];
  return Array.isArray(actions) && actions.includes(action);
}

/**
 * True when the baked map has no nav_* keys — an old JWT from before this catalog.
 * After backfill every role gets at least nav_dashboard + nav_help, so a saved
 * matrix that omitted a new feature is never treated as legacy.
 */
export function isLegacyPermissionMap(map: PermissionMap | null | undefined): boolean {
  if (!map) return true;
  return !NAV_PERMISSION_RESOURCES.some((resource) => mapHasResourceKey(map, resource));
}

export function communicationTabGranted(map: PermissionMap | null | undefined): boolean {
  if (permissionMapHas(map, 'communication', 'view')) return true;
  return false;
}

/**
 * Old-to-new Kommunikation mapping used when backfilling and when a JWT has
 * no `communication` key yet: any of SMS / Beskeder / E-mail view.
 */
export function communicationTabFromLegacy(map: PermissionMap | null | undefined): boolean {
  return LEGACY_COMMUNICATION_TAB_RESOURCES.some((resource) =>
    permissionMapHasExact(map, resource, 'view'),
  );
}

function legacyAnyGranted(
  map: PermissionMap,
  resources: readonly PermissionResource[],
  action: PermissionAction,
): boolean {
  return resources.some((resource) => permissionMapHasExact(map, resource, action));
}

export function legacyPermissionFallback(
  map: PermissionMap,
  resource: PermissionResource,
  action: PermissionAction,
): boolean {
  switch (resource) {
    case 'communication':
      return action === 'view' && communicationTabFromLegacy(map);
    case 'patient_assignments':
      if (action === 'view') return permissionMapHasExact(map, 'patients', 'view');
      return permissionMapHasExact(map, 'patients', 'update');
    case 'patient_my_filter':
      return action === 'view' && permissionMapHasExact(map, 'patients', 'view');
    case 'journal_phrases':
      if (action === 'view' || action === 'create' || action === 'update') {
        return permissionMapHasExact(map, 'journal', 'view');
      }
      return false;
    case 'medcom':
      return (
        (action === 'view' || action === 'create') && permissionMapHasExact(map, 'patients', 'view')
      );
    case 'medcom_xdis':
      return permissionMapHasExact(map, 'referrals', action === 'create' ? 'create' : 'view');
    case 'medcom_ggop':
      return permissionMapHasExact(map, 'referrals', 'view');
    case 'medcom_sup':
      return false;
    case 'nav_dashboard':
    case 'nav_help':
      return action === 'view';
    case 'nav_patients':
      return action === 'view' && permissionMapHasExact(map, 'patients', 'view');
    case 'nav_messages':
      return action === 'view' && permissionMapHasExact(map, 'messages', 'view');
    case 'nav_referrals':
      return action === 'view' && permissionMapHasExact(map, 'referrals', 'view');
    case 'nav_medcom':
      return action === 'view' && permissionMapHasExact(map, 'patients', 'view');
    case 'treatment_courses':
      return permissionMapHasExact(
        map,
        'appointments',
        action === 'view' ? 'view' : action === 'update' || action === 'create' ? action : 'update',
      );
    case 'nav_appointments':
      return action === 'view' && permissionMapHasExact(map, 'appointments', 'view');
    case 'nav_economy':
      return action === 'view' && legacyAnyGranted(map, ECONOMY_PERMISSION_RESOURCES, 'view');
    case 'nav_settings':
      return action === 'view' && permissionMapHasExact(map, 'settings_general', 'view');
    default:
      return false;
  }
}

export function permissionMapHas(
  map: PermissionMap | null | undefined,
  resource: PermissionResource,
  action: PermissionAction,
): boolean {
  if (!map) return false;
  if (mapHasResourceKey(map, resource)) {
    return permissionMapHasExact(map, resource, action);
  }
  if (!isLegacyPermissionMap(map)) return false;
  return legacyPermissionFallback(map, resource, action);
}

export function navItemGranted(
  map: PermissionMap | null | undefined,
  item: NavMenuItem,
): boolean {
  return permissionMapHas(map, item.id, 'view');
}

export function emptyPermissionMap(): PermissionMap {
  const map: PermissionMap = {};
  for (const resource of PERMISSION_RESOURCES) {
    map[resource] = [];
  }
  return map;
}

export function normalizePermissionMap(raw: unknown): PermissionMap {
  const map = emptyPermissionMap();
  if (!raw || typeof raw !== 'object') return map;
  const obj = raw as Record<string, unknown>;
  for (const resource of PERMISSION_RESOURCES) {
    const actions = obj[resource];
    if (!Array.isArray(actions)) continue;
    map[resource] = actions.filter(
      (a): a is PermissionAction =>
        typeof a === 'string' && (PERMISSION_ACTIONS as readonly string[]).includes(a),
    );
  }
  return map;
}

export type PatientTabPermissionKey =
  | 'appointments'
  | 'treatment_courses'
  | 'activity'
  | 'journal'
  | 'documents'
  | 'invoices'
  | 'punch_cards'
  | 'sygeforsikring'
  | 'communication'
  | 'consent'
  | 'tasks'
  | 'questionnaires'
  | 'dental'
  | 'henvisning'
  | 'sagsakter'
  | 'tilknytninger'
  | 'relations'
  | 'medcom';

export const PATIENT_TAB_PERMISSION_RESOURCE: Record<
  PatientTabPermissionKey,
  PermissionResource | null
> = {
  appointments: 'appointments',
  treatment_courses: 'treatment_courses',
  activity: null,
  journal: 'journal',
  documents: 'documents',
  invoices: 'invoices',
  punch_cards: 'punch_cards',
  sygeforsikring: 'sygeforsikring',
  communication: 'communication',
  consent: 'consent',
  tasks: 'tasks',
  questionnaires: 'questionnaires',
  dental: 'dental',
  henvisning: 'referrals',
  sagsakter: 'referrals',
  tilknytninger: 'patient_assignments',
  relations: 'relations',
  medcom: 'medcom',
};

export function patientTabPermissionGranted(
  map: PermissionMap | null | undefined,
  tab: PatientTabPermissionKey,
): boolean {
  const resource = PATIENT_TAB_PERMISSION_RESOURCE[tab];
  if (!resource) return false;
  return permissionMapHas(map, resource, 'view');
}
